import React, { useState, useEffect, useMemo } from "react";
import MyAccountTemplate from "../../sections/my-account-template";
import Heading from "../../elements/heading";
import PrimaryButton from "../../elements/primary-button";
import BodyText from "../../elements/body-text";
import ConfirmationModal from "../../react-components/confirmation-modal";
import "./style.scss";
import { connect } from "react-redux";
import { staffInfoSelector, selectUserState } from "../../selectors/application";
import { genrateHeadingWithFirstName } from "../../utils/myAccount";
import api from "../../utils/api";
import { getApiError } from "../../services/siteService";
import Icon from "../../elements/icon";
import BankCardModal from "../../sections/bank-card-modal";
import { PrimaryLink } from "../../elements";
import { isGlobalSite } from "../../utils/siteUtils";
import { bankCardSubTypes } from "../../utils/lynkco-constants";
import { S_EDITING_CARD, webStorage } from "../../utils/webStorage";
import { getBankCardOptions } from "../../services/userService";
import AccountBankCardInformation from "../../sections/account-bank-card-information";

const MyAccountPayMethods = ({ rendering, fields, user }) => {
  const { accountMenu, hero, savedPaymentTitleType, savedPaymentTitleBody, savedPaymentBody, cardNumberLabel, updateCardModal, ctaAddCard, addCardModal, noPayMethodIcon, noPayMethodBody, confirmationModal, activedCardUndeletableMessage, disclaimer, cardExpiredText, primaryBtn } = fields;

  const { firstName } = user || {};
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showBankCardModal, setShowBankCardModal] = useState(false);
  const [bankCards, setBankCards] = useState([]);
  const [selectedBankCard, setSelectedBankCard] = useState(null);
  const [removeCardError, setRemoveCardError] = useState(null);
  const [cardModalFields, setCardModalFields] = useState(addCardModal.fields);
  const [isDisabled, setIsDisabled] = useState(false);
  const activeCard = useMemo(() => bankCards.find(x => x.data.cardSubType === bankCardSubTypes.actived), [bankCards]);
  const primaryCard = useMemo(() => bankCards.find(x => x.data.defaultFlag == "1"), [bankCards]);
  useEffect(() => {
    getBankCards();
    return () => {
      webStorage.clear(S_EDITING_CARD);
    };
  }, []);
  const getBankCards = async () => {
    const _bankCards = await getBankCardOptions();
    if (Array.isArray(_bankCards)) {
      setBankCards(_bankCards);
      const _bankCard = _bankCards.find(x => x.data.defaultFlag === "1");
      if (_bankCard) {
        setSelectedBankCard(_bankCard);
      }
    } else {
      setBankCards([]);
      setSelectedBankCard(null);
    }
  };
  const onEditCardClick = async (evt, card) => {
    if (card.data.cardSubType === bankCardSubTypes.actived) {
      evt.stopPropagation();
      setCardModalFields(updateCardModal.fields);
      webStorage.set(S_EDITING_CARD, card);
      setShowBankCardModal(true);
    }
  };
  const onRemoveCardClick = (evt, card) => {
    if (card.data.cardSubType !== bankCardSubTypes.actived) {
      evt.stopPropagation();
      webStorage.set(S_EDITING_CARD, card);
      setShowConfirmationModal(true);
    }
  };
  const saveRemoveCard = async () => {
    const card = webStorage.get(S_EDITING_CARD);
    try {
      await api.user.deleteBankCard(null, { bankCardIds: [card.value] });
      if (card.data.defaultFlag == "1") {
        if (bankCards.length > 1) {
          await setPrimaryCard(card.nextCard);
        }
      }
      await getBankCards();
      setShowConfirmationModal(false);
      webStorage.clear(S_EDITING_CARD);
    } catch (ex) {
      switch (ex.code) {
        case "USER_CUSTOMER_0041":
          setRemoveCardError(activedCardUndeletableMessage?.value || "System error");
          break;
        default:
          const error = await getApiError(ex.code);
          setRemoveCardError(error?.body?.value || "System error");
          break;
      }
    }
  };
  const setPrimaryCard = async cardData => {
    setIsDisabled(true);
    await api.user.updateBankCard(null, {
      ...cardData,
      defaultFlag: "1"
    });
    setIsDisabled(false);
  };
  const onBankCardChange = async card => {
    setSelectedBankCard(card);
    await setPrimaryCard(card.data);
    await getBankCards();
  };
  const bankCardModalProps = {
    fields: cardModalFields,
    visible: showBankCardModal,
    onSuccess: async () => {
      await getBankCards();
      webStorage.clear(S_EDITING_CARD);
      setShowBankCardModal(false);
    },
    onCloseClicked: () => {
      webStorage.clear(S_EDITING_CARD);
      setShowBankCardModal(false);
    }
  };
  const ctaAddCardProps = {
    colorScheme: ctaAddCard.fields.colorScheme,
    iconName: ctaAddCard.fields.iconName,
    buttonText: ctaAddCard.fields.ctaName.value,
    callback: evt => {
      if (activeCard) {
        evt.stopPropagation();
        setCardModalFields(updateCardModal.fields);
        webStorage.set(S_EDITING_CARD, activeCard);
        setShowBankCardModal(true);
      } else {
        setCardModalFields(addCardModal.fields);
        webStorage.clear(S_EDITING_CARD);
        setShowBankCardModal(true);
      }
    }
  };
  return (
    <div>
      <MyAccountTemplate
        heroConfiguration={
          hero[0].fields
            ? {
                ...hero[0].fields,
                titleBody: genrateHeadingWithFirstName(hero[0].fields.titleBody, firstName)
              }
            : {}
        }
        menu={accountMenu ? accountMenu[0].fields : {}}
        datasourceid={rendering.dataSource}
      >
        <div className="my-account-pay-methods">
          <section className="saved-payment-method">
            <Heading type={savedPaymentTitleType} html={savedPaymentTitleBody} typographyClass="type-h3" />
            <BodyText html={savedPaymentBody.value} />
            {bankCards.length === 0 && (
              <div className="no-payment-method">
                <Icon icon={noPayMethodIcon.value} colorScheme="grey" />
                <BodyText html={noPayMethodBody.value} className="no-payment-method-body" />
              </div>
            )}
            <div className="card-list">
              {bankCards.map((card, index) => {
                const iconColorScheme = card.data.cardSubType === bankCardSubTypes.actived ? "grey" : "outline-green";
                const showIcon = card.data.cardSubType === bankCardSubTypes.actived || (activeCard == null && card.data.defaultFlag == "1");
                return (
                  <div className="card-item" key={card.value + "-" + index}>
                    <AccountBankCardInformation {...{ card: card.data, expiredText: cardExpiredText.value, showIcon }}>
                      <div className="icons">{<Icon name="Delete" colorScheme={iconColorScheme} className="icon --visible" onClick={evt => onRemoveCardClick(evt, card)} />}</div>
                    </AccountBankCardInformation>
                  </div>
                );
              })}
            </div>
            {!isGlobalSite() && <PrimaryButton {...ctaAddCardProps} />}
            {showBankCardModal && <BankCardModal {...bankCardModalProps} />}
            <BodyText className="disclaimer" html={disclaimer.value} />
            <div className="link-container">{primaryBtn.fields && <PrimaryLink {...primaryBtn.fields} />}</div>
          </section>
        </div>
      </MyAccountTemplate>
      <ConfirmationModal
        {...{
          heading: {
            type: confirmationModal.fields.headingType,
            html: confirmationModal.fields.headingBody
          },
          subHeading: {
            type: confirmationModal.fields.subheadingType,
            html: confirmationModal.fields.subheadingBody
          },
          body: confirmationModal.fields.body.value,
          button: {
            ...{
              colorScheme: confirmationModal.fields.colorScheme,
              iconName: confirmationModal.fields.iconName,
              link: {
                value: {
                  text: confirmationModal.fields.ctaName.value
                }
              },
              callback: evt => saveRemoveCard()
            }
          },
          warningMessage: removeCardError,
          onCloseClicked: () => {
            webStorage.clear(S_EDITING_CARD);
            setShowConfirmationModal(false);
            setRemoveCardError(null);
          },
          showModal: showConfirmationModal
        }}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  staffInfo: staffInfoSelector(state),
  user: selectUserState(state)
});

export default connect(mapStateToProps)(MyAccountPayMethods);
